// Snapshot ↔ snapshot content diff drawer (read-only grid + CSV export).
import { useMemo, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DataSheetGrid,
  keyColumn,
  type CellProps,
  type Column,
} from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";
import "./snapshot-diff-grid.css";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Button, Spinner } from "../../components/ui.js";

export interface SnapshotListItem {
  id: string;
  createdAt: string;
  lane: string;
  trigger: string;
  actorEmail: string;
  label: string | null;
  rowCounts: Record<string, number>;
  ref: string;
}

interface SnapshotDiffRow {
  _change: "added" | "removed" | "changed";
  _id: string;
  _fieldsChanged?: string;
  _before?: string;
  _after?: string;
  [key: string]: unknown;
}

interface TableDiff {
  name: string;
  beforeCount: number;
  afterCount: number;
  added: number;
  removed: number;
  changed: number;
  sampleRows: SnapshotDiffRow[];
  truncated: boolean;
}

interface SnapshotDiffResult {
  before: SnapshotListItem;
  after: SnapshotListItem;
  beforeTotal: number;
  afterTotal: number;
  tables: TableDiff[];
  canary: {
    before: Array<{ email: string; canary: string }>;
    after: Array<{ email: string; canary: string }>;
  };
}

type FontSize = "sm" | "md" | "lg";

const FONT_PX: Record<FontSize, number> = { sm: 12, md: 13, lg: 14 };
const ROW_HEIGHT: Record<FontSize, number> = { sm: 28, md: 32, lg: 36 };
const HEADER_HEIGHT: Record<FontSize, number> = { sm: 28, md: 30, lg: 32 };
const WRAP_MAX_HEIGHT = 160;
const ALWAYS_KEEP = new Set(["_change", "_id", "_fieldsChanged", "_before", "_after"]);

function snapLabel(s: SnapshotListItem): string {
  return s.label || s.trigger || s.ref;
}

/** Chronologically previous snapshot (older createdAt) relative to `selected`. */
export function previousSnapshot(
  selected: SnapshotListItem,
  all: SnapshotListItem[],
): SnapshotListItem | null {
  const older = all
    .filter((s) => s.ref !== selected.ref && s.createdAt < selected.createdAt)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return older[0] ?? null;
}

function cellEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

function allKeysInSample(rows: SnapshotDiffRow[]): string[] {
  const preferred = ["_change", "_id", "_fieldsChanged"];
  const rest = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!preferred.includes(k) && k !== "_before" && k !== "_after") rest.add(k);
    }
  }
  const mid = [...rest].sort((a, b) => {
    const aBefore = a.startsWith("before.") ? 1 : 0;
    const bBefore = b.startsWith("before.") ? 1 : 0;
    if (aBefore !== bBefore) return aBefore - bBefore;
    return a.localeCompare(b);
  });
  const keys = [...preferred, ...mid];
  if (rows.some((r) => r._before != null)) keys.push("_before");
  if (rows.some((r) => r._after != null)) keys.push("_after");
  return keys;
}

function columnKeysFor(rows: SnapshotDiffRow[], hideEmpty: boolean): {
  keys: string[];
  hiddenCount: number;
} {
  const all = allKeysInSample(rows);
  if (!hideEmpty) return { keys: all, hiddenCount: 0 };
  const keys = all.filter((k) => {
    if (ALWAYS_KEEP.has(k)) return true;
    return rows.some((r) => !cellEmpty(r[k]));
  });
  return { keys, hiddenCount: all.length - keys.length };
}

function DisplayCell({
  rowData,
}: CellProps<string | null, unknown>): JSX.Element {
  return <div className="diff-cell">{rowData ?? ""}</div>;
}

function ChangePillCell({
  rowData,
}: CellProps<string | null, unknown>): JSX.Element {
  const v = rowData ?? "";
  const tone =
    v === "added" ? "added" : v === "removed" ? "removed" : v === "changed" ? "changed" : "changed";
  return (
    <div className="diff-cell">
      {v ? <span className={`diff-pill diff-pill-${tone}`}>{v}</span> : null}
    </div>
  );
}

function SoftPillCell({
  rowData,
  columnData,
}: CellProps<string | null, { kind: "status" | "domain" }>): JSX.Element {
  const v = (rowData ?? "").trim();
  if (!v) return <div className="diff-cell" />;
  // Long free-text values stay plain; short enums get Airtable-ish pills.
  if (v.length > 28) return <div className="diff-cell">{v}</div>;
  return (
    <div className="diff-cell">
      <span className={`diff-pill diff-pill-${columnData.kind}`}>{v}</span>
    </div>
  );
}

const displayTextColumn: Partial<Column<string | null, unknown, string>> = {
  component: DisplayCell,
  disabled: true,
  deleteValue: () => null,
  copyValue: ({ rowData }) => (rowData == null ? null : String(rowData)),
  pasteValue: ({ value }) => value,
  isCellEmpty: ({ rowData }) => cellEmpty(rowData),
};

const changePillColumn: Partial<Column<string | null, unknown, string>> = {
  ...displayTextColumn,
  component: ChangePillCell,
};

function softPillColumn(
  kind: "status" | "domain",
): Partial<Column<string | null, { kind: "status" | "domain" }, string>> {
  return {
    ...displayTextColumn,
    component: SoftPillCell,
    columnData: { kind },
  };
}

function columnBasis(k: string): { minWidth: number; basis: number; grow: number } {
  if (k === "_change") return { minWidth: 88, basis: 88, grow: 0 };
  if (k === "_id") return { minWidth: 130, basis: 140, grow: 0 };
  if (k === "_fieldsChanged") return { minWidth: 120, basis: 160, grow: 1 };
  if (k === "_before" || k === "_after") return { minWidth: 140, basis: 200, grow: 1 };
  return { minWidth: 100, basis: 140, grow: 1 };
}

function estimateWrappedRowHeight(
  row: Record<string, string | null>,
  keys: string[],
  fontPx: number,
  baseHeight: number,
): number {
  const lineHeight = fontPx * 1.35;
  const pad = 8;
  let maxLines = 1;
  for (const k of keys) {
    const text = row[k] ?? "";
    if (!text) continue;
    // Approximate visible column width from basis (DSG flex-shrinks; good enough for layout).
    const colW = columnBasis(k).basis;
    const charsPerLine = Math.max(8, Math.floor(colW / (fontPx * 0.55)));
    const lines = Math.ceil(text.length / charsPerLine);
    if (lines > maxLines) maxLines = lines;
  }
  return Math.min(WRAP_MAX_HEIGHT, Math.max(baseHeight, Math.ceil(pad + maxLines * lineHeight)));
}

function DiffGrid({ rows }: { rows: SnapshotDiffRow[] }): JSX.Element {
  const [hideEmpty, setHideEmpty] = useState(true);
  const [wrap, setWrap] = useState(false);
  const [fontSize, setFontSize] = useState<FontSize>("sm");

  const { keys, hiddenCount } = useMemo(
    () => columnKeysFor(rows, hideEmpty),
    [rows, hideEmpty],
  );

  const data = useMemo(
    () =>
      rows.map((row) => {
        const out: Record<string, string | null> = {};
        for (const [k, v] of Object.entries(row)) {
          out[k] = v == null ? null : typeof v === "string" ? v : String(v);
        }
        return out as SnapshotDiffRow;
      }),
    [rows],
  );

  const columns = useMemo(() => {
    return keys.map((k) => {
      const sizes = columnBasis(k);
      const col =
        k === "_change"
          ? changePillColumn
          : k === "status" || k === "Status"
            ? softPillColumn("status")
            : k === "domain" || k === "Domain"
              ? softPillColumn("domain")
              : displayTextColumn;

      return {
        ...keyColumn(k, col as Partial<Column<string | null, unknown, string>>),
        title: k,
        disabled: true,
        minWidth: sizes.minWidth,
        basis: sizes.basis,
        grow: sizes.grow,
        shrink: 1,
      };
    });
  }, [keys]);

  const baseRowHeight = ROW_HEIGHT[fontSize];
  const fontPx = FONT_PX[fontSize];

  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">No row-level changes in this table sample.</p>;
  }

  const wrapperStyle = {
    "--diff-grid-font-size": `${fontPx}px`,
  } as CSSProperties;

  return (
    <div className="space-y-2">
      <div className="snapshot-diff-grid-toolbar">
        <button
          type="button"
          className="diff-toggle"
          aria-pressed={hideEmpty}
          onClick={() => setHideEmpty((v) => !v)}
        >
          Hide empty
        </button>
        {hideEmpty && hiddenCount > 0 ? (
          <span className="diff-hidden-count">{hiddenCount} hidden</span>
        ) : null}
        <button
          type="button"
          className="diff-toggle"
          aria-pressed={wrap}
          onClick={() => setWrap((v) => !v)}
        >
          Wrap
        </button>
        <div className="diff-font-group" role="group" aria-label="Font size">
          {(["sm", "md", "lg"] as const).map((size) => (
            <button
              key={size}
              type="button"
              aria-pressed={fontSize === size}
              aria-label={size === "sm" ? "Small font" : size === "md" ? "Medium font" : "Large font"}
              onClick={() => setFontSize(size)}
            >
              {size === "sm" ? "Aa−" : size === "md" ? "Aa" : "Aa+"}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`snapshot-diff-grid overflow-auto${wrap ? " snapshot-diff-grid--wrap" : ""}`}
        style={wrapperStyle}
      >
        <DataSheetGrid
          // Remount when density mode changes — DSG caches variable row heights in a ref
          // that does not fully reset when switching between fixed and dynamic heights.
          key={`diff-grid-${wrap ? "wrap" : "clip"}-${fontSize}-${keys.length}`}
          value={data}
          onChange={() => {
            /* read-only */
          }}
          columns={columns}
          lockRows
          addRowsComponent={false}
          disableContextMenu
          height={420}
          headerRowHeight={HEADER_HEIGHT[fontSize]}
          rowHeight={({ rowData }) =>
            wrap
              ? estimateWrappedRowHeight(
                  rowData as Record<string, string | null>,
                  keys,
                  fontPx,
                  baseRowHeight,
                )
              : baseRowHeight
          }
          rowKey={({ rowData }) => String((rowData as SnapshotDiffRow)._id)}
        />
      </div>
    </div>
  );
}

export function SnapshotDiffDrawer({
  selected,
  snapshots,
  onClose,
}: {
  selected: SnapshotListItem;
  snapshots: SnapshotListItem[];
  onClose: () => void;
}): JSX.Element {
  const defaultB =
    previousSnapshot(selected, snapshots) ??
    snapshots
      .filter((s) => s.ref !== selected.ref)
      .sort((x, y) => y.createdAt.localeCompare(x.createdAt))[0] ??
    null;
  const [bRef, setBRef] = useState(defaultB?.ref ?? "");
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const a = selected.ref;
  const b = bRef;
  const canDiff = Boolean(a && b && a !== b);

  const diffQ = useQuery({
    queryKey: qk.adminDataOpsDiff(a, b),
    queryFn: () =>
      api.get<SnapshotDiffResult>(
        `/admin/dataops/snapshots/diff?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}&maxRowsPerTable=500`,
      ),
    enabled: canDiff,
    staleTime: 60_000,
  });

  const tables = diffQ.data?.tables ?? [];
  const tab = activeTable && tables.some((t) => t.name === activeTable) ? activeTable : tables[0]?.name ?? null;
  const active = tables.find((t) => t.name === tab) ?? null;

  const others = snapshots
    .filter((s) => s.ref !== selected.ref)
    .sort((x, y) => y.createdAt.localeCompare(x.createdAt));

  async function exportCsv(): Promise<void> {
    if (!tab || !canDiff) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const res = await fetch(
        `/api/admin/dataops/snapshots/diff.csv?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}&table=${encodeURIComponent(tab)}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        let message = `Export failed (${res.status})`;
        try {
          const body = (await res.json()) as { error?: string };
          if (body.error) message = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `snapshot-diff_${tab}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setCsvError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setCsvBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Snapshot diff"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-6xl flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0 space-y-1">
            <h2 className="text-base font-semibold">
              Diff: {snapLabel(selected)}{" "}
              <span className="font-normal text-slate-400">vs</span>{" "}
              {b ? snapLabel(others.find((s) => s.ref === b) ?? { ...selected, ref: b, label: b }) : "—"}
            </h2>
            <p className="text-xs text-slate-500">
              Row counts ≠ correctness. Prefer editorial tables + canary; ignore mass{" "}
              <span className="font-mono">raw</span>-only referee/people noise unless investigating sync
              mapping.
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
              <label className="flex items-center gap-1.5 text-slate-600">
                Compare against
                <select
                  className="rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs"
                  value={b}
                  onChange={(e) => {
                    setBRef(e.target.value);
                    setActiveTable(null);
                  }}
                >
                  {others.length === 0 ? (
                    <option value="">No other snapshots</option>
                  ) : (
                    others.map((s) => (
                      <option key={s.ref} value={s.ref}>
                        {snapLabel(s)} · {new Date(s.createdAt).toLocaleString()} · {s.ref}
                      </option>
                    ))
                  )}
                </select>
              </label>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>

        <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
          {!canDiff ? (
            <p className="text-sm text-slate-500">Pick another snapshot to compare against.</p>
          ) : diffQ.isLoading ? (
            <Spinner label="Computing diff (loading snapshots server-side)…" />
          ) : diffQ.isError ? (
            <p className="text-sm text-red-600">
              {diffQ.error instanceof ApiError
                ? diffQ.error.message
                : diffQ.error instanceof Error
                  ? diffQ.error.message
                  : "Diff failed"}
            </p>
          ) : diffQ.data ? (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge tone="slate">
                  rows {diffQ.data.beforeTotal} → {diffQ.data.afterTotal}
                </Badge>
                <Badge tone="blue">{diffQ.data.tables.length} tables changed</Badge>
                {diffQ.data.canary.after[0] ? (
                  <Badge tone="green">
                    canary: {diffQ.data.canary.after[0].canary.slice(0, 48)}
                    {diffQ.data.canary.after[0].canary.length > 48 ? "…" : ""}
                  </Badge>
                ) : null}
              </div>

              {tables.length === 0 ? (
                <p className="text-sm text-slate-500">No content differences between these snapshots.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1 border-b border-slate-100 pb-2">
                    {tables.map((t) => (
                      <button
                        key={t.name}
                        type="button"
                        className={`rounded px-2 py-1 text-xs font-medium ${
                          t.name === tab
                            ? "bg-slate-900 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                        onClick={() => setActiveTable(t.name)}
                      >
                        {t.name}{" "}
                        <span className="opacity-70">
                          +{t.added}/−{t.removed}/~{t.changed}
                        </span>
                      </button>
                    ))}
                  </div>

                  {active ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-slate-500">
                          {active.beforeCount} → {active.afterCount} rows · sample{" "}
                          {active.sampleRows.length}
                          {active.truncated ? " (truncated — export CSV for more)" : ""}
                        </p>
                        <Button variant="secondary" disabled={csvBusy} onClick={() => void exportCsv()}>
                          {csvBusy ? "Exporting…" : "Export CSV"}
                        </Button>
                      </div>
                      {csvError ? <p className="text-xs text-red-600">{csvError}</p> : null}
                      <DiffGrid rows={active.sampleRows} />
                    </div>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
